import React, {
    useEffect,
    useRef,
    useState,
} from 'react';

import { Auth } from 'aws-amplify';
import { useIdleTimer } from 'react-idle-timer';
import {
    Navigate,
    Route,
    Routes,
    useLocation,
    useNavigate,
} from 'react-router-dom';

import Layout from '../components/Layout/Layout';
import SessionTimeoutModal from '../components/SessionTimeoutModal/SessionTimeoutModal';
import {
    _setStorageTimeout,
    _storageTimeoutExpired,
} from '../utils/helpers';
import LayoutEmpty from '../components/Layout/LayoutEmpty';
import { useDispatch, useSelector } from 'react-redux';
import { getUserDetailsFromSession, logoutAccount, sendGADimensions, setLoggedUserDetailsSuccess } from '../actions/Users/UserAction';
import { BroadcastChannel } from 'broadcast-channel'; import {
    getCasSearch,
    getOrgsSearch,
    getStatusesSearch,
    getUserRolesSearch,
} from '../actions/Users/UserAction';
import {
    ADMINISTRATOR_USER,
    ASSOCIATION_USER,
    SUPER_USER,
} from '../actionTypes/Users/UserActionType';
import { fetchSessionUserInstitutions } from '../actions/Users/UserAction';
import { fetchAssociationsList } from '../actions/AssociationAndOrg/AssociationOrgAction';

const hiddenLinks = ['demo'];
const inactiveTimeAllowed = 30 * 60 * 1000;

export const G_MSG_CHANNEL = new BroadcastChannel('analytics-channel');

const PRoute = ({ children, linkName, path, useEmptyLayout = false, ...rest }) => {
    const { email: currentEmail, userRole, } = useSelector((state) => state.AppSession.userDetails);
    const { userPersonated } = useSelector((state) => state.Impersonate.impersonate);
    const [loaded, setLoaded] = useState(false);
    const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);
    const [canViewHiddenPages, setCanViewHiddenPages] = useState(false);
    const [isHiddenLink, setIsHiddenLink] = useState(false);
    const [showSessionTimeoutModal, setShowSessionTimeoutModal] = useState(false);
    const [isTimedOut, setIsTimedOut] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const idle = useIdleTimer({
        element: document,
        onIdle: () => _onIdle(),
        onAction: () => _onAction(),
        debounce: 250,
        timeout: inactiveTimeAllowed
    });

    const authenticate = async () => {
        try {
            await Auth.currentAuthenticatedUser();
            setIsUserAuthenticated(true);
            setLoaded(true);
            _setStorageTimeout(inactiveTimeAllowed);
            const userDetails = await getUserDetailsFromSession();
            dispatch(setLoggedUserDetailsSuccess(userDetails));
        } catch (err) {
            console.error(err);
            signOut();
        }
    }

    const _onAction = (_) => {
        if (isTimedOut) {
            setIsTimedOut(false);
        } else {
            _setStorageTimeout(inactiveTimeAllowed);
        }
    };

    const _onIdle = () => {
        if (!isTimedOut && !showSessionTimeoutModal) {
            setShowSessionTimeoutModal(true);
            setIsTimedOut(true);
            idle.pause();
        }
    };

    const signOut = (status = '') => {
        navigate(`/login${status ? '?status=' + status : ''}`);
        dispatch(logoutAccount());
        setIsUserAuthenticated(false);
        sendGADimensions();
        _setStorageTimeout();
    };

    const processMessage = (message) => {
        const { action, userEmail: nextUserEmail, impersonateStatus } = message;
        switch (action) {
            case 'logout': {
                !currentEmail || currentEmail !== nextUserEmail ? signOut('logout-account') : void 0;
                break;
            }
            case 'refresh': {
                break;
            }
        }
    };

    useEffect(() => {
        G_MSG_CHANNEL.onmessage = processMessage;
    }, []);

    useEffect(() => {
        if (_storageTimeoutExpired()) {
            signOut('timeout');
        } else {
            isUserAuthenticated ? void 0 : authenticate();
        }
        setCanViewHiddenPages(/^local|^qa/.test(document.location.hostname));
        setIsHiddenLink(hiddenLinks.includes(linkName));
    }, [path, linkName]);

    useEffect(() => {
        !currentEmail && isUserAuthenticated ? signOut() : void 0;
        if (userRole === SUPER_USER && linkName === 'home') {
            navigate('/users');
        }
    }, [currentEmail, userRole]);

    useEffect(() => {
        processMessage({
            action: 'refresh',
            impersonateStatus: !!userPersonated
        });

        userPersonated ? (async () => {
            const awsUser = await Auth.currentAuthenticatedUser();
            const loggedAs = awsUser.signInUserSession.idToken.payload['custom:userRole'];
            const strURL = document.URL
            const urlObj = new URL(strURL);
            let pushUrl = '/'
            if (urlObj.pathname.includes('/ui/reports')) {
                let paramSub = urlObj.searchParams.get('sub')
                let paramMain = urlObj.searchParams.get('main')
                pushUrl += 'reports'
                pushUrl += paramMain ? '?main=' + paramMain : ''
                pushUrl += paramSub ? '&sub=' + paramSub : ''
            }
            switch (loggedAs) {
                case SUPER_USER:

                    pushUrl.includes('reports') ? navigate(pushUrl) : navigate('/users');
                    dispatch(fetchAssociationsList());
                    dispatch(getCasSearch());
                    dispatch(getOrgsSearch());
                    dispatch(getStatusesSearch());
                    dispatch(getUserRolesSearch());
                    break;
                case ASSOCIATION_USER:
                    //navigate('/');
                    navigate(pushUrl)
                    dispatch(fetchSessionUserInstitutions());
                    dispatch(fetchAssociationsList());
                    break;
                case ADMINISTRATOR_USER:
                    //navigate('/');
                    navigate(pushUrl)
                    dispatch(fetchSessionUserInstitutions());
                    dispatch(fetchAssociationsList());
                    dispatch(getCasSearch());
                    dispatch(getOrgsSearch());
                    dispatch(getStatusesSearch());
                    dispatch(getUserRolesSearch());
                    break;
                default:
                    navigate(pushUrl);
                    dispatch(fetchSessionUserInstitutions());
                    dispatch(fetchAssociationsList());
                    break;
            }
        })() : void 0;

    }, [userPersonated]);
    const location = useLocation();
    return loaded ?
        <React.Fragment>

            {
                (isHiddenLink ? canViewHiddenPages : isUserAuthenticated) ? (
                    useEmptyLayout ?
                        <LayoutEmpty location={location}>{children}</LayoutEmpty> :
                        <Layout location={location} linkName={linkName} {...rest}>
                            {children}
                        </Layout>
                ) : (
                    <Navigate
                        to={{
                            pathname: isHiddenLink ? '/' : '/login',
                        }}
                    />
                )
            }
            <SessionTimeoutModal
                showModal={showSessionTimeoutModal}
                handleLogout={() => signOut('timeout')}
                handleClose={() => {
                    setShowSessionTimeoutModal(false);
                    setIsTimedOut(false);
                    idle.reset();
                }}
            />
        </React.Fragment> : null;
};

export default PRoute;
